'use client';

import { isEqual } from 'es-toolkit';
import EventEmitter from 'eventemitter3';
import { t } from 'mobx-state-tree';

import { type IPropertyStore } from '../propertyStore';

import type { PropertyChangeEvent as PropertyEvent, ValueChangeContext, ValueChangeHandler } from '../types';
import type { PropertyMetadata } from '@gnsx/genesys.js';

import { DEFAULT_DISPLAY_ORDER } from '@/app/editor/core/editorClassMeta';

/** Base model for all property editor properties */
export const PropertyBase = t
  .model('PropertyBase', {
    key: t.identifier, // Unique key for the property
    path: t.string, // Path to the property in the object
    parentPath: t.maybe(t.string), // Path to the parent property
    label: t.string, // Display name label for the property
    tooltip: t.maybe(t.string),
    disabled: t.optional(t.boolean, false),
    readOnly: t.optional(t.boolean, false),
    hidden: t.optional(t.boolean, false),
    order: t.optional(t.number, DEFAULT_DISPLAY_ORDER.default),
    meta: t.maybe(t.frozen<PropertyMetadata>()),
    isOverride: t.optional(t.boolean, false),
  })
  .volatile(() => ({
    eventEmitter: new EventEmitter(),
    store: undefined as IPropertyStore | undefined
  }))
  .views(self => ({
    get debugName(): string { return `${self.label} (${self.path})`; },
    get fullPath(): string {
      return self.parentPath && !self.path.startsWith(self.parentPath) ? `${self.parentPath}.${self.path}` : self.path;
    },
    get hasValueChanged(): boolean {
      if (!('defaultValue' in self) || !('value' in self)) return false;
      return !isEqual((self as any).value, (self as any).defaultValue);
    },
    /**
     * True when the property is marked required and its current value is null, undefined, or empty string.
     * Used by property view components to show a warning indicator on the field label.
     */
    get isRequiredEmpty(): boolean {
      if (!self.meta?.required) return false;
      if (!('value' in self)) return false;
      const v = (self as any).value;
      return v === null || v === undefined || v === '';
    },
    get canResetToDefaultValue(): boolean {
      // Enable reset button whenever there's a defaultValue set and value has changed
      // This now works for both prefab overrides (isOverride=true) and metadata-based defaults
      // Don't show reset button for readonly properties
      if (self.readOnly) return false;
      if (!('defaultValue' in self) || !('value' in self)) return false;
      return this.hasValueChanged;
    }
  }))
  .actions(self => ({
    afterCreate() {
      // TODO: broadcast initial value
      if (!self.tooltip) self.tooltip = self.fullPath;
    },
    setStore(store: IPropertyStore) {
      self.store = store;
    },
    setDisabled(disabled: boolean) {
      self.disabled = disabled;
    },
    setHidden(hidden: boolean) {
      self.hidden = hidden;
    },
    setLabel(label: string) {
      self.label = label;
    },
    setTooltip(tooltip?: string) {
      self.tooltip = tooltip;
    },
    setOrder(order: number) {
      self.order = order;
    },
    setIsOverride(isOverride: boolean) {
      self.isOverride = isOverride;
    },
    resetToDefault() {
      // Default implementation - can be overridden by specific property types
      if ('defaultValue' in self && 'setValue' in self) {
        const defaultValue = (self as any).defaultValue;
        (self as any).setValue(defaultValue, true, false);
      }
    },
    /** Add event listener */
    on(event: PropertyEvent, handler: ValueChangeHandler) {
      self.eventEmitter.on(event, handler);
    },
    /** Remove event listener */
    off(event: PropertyEvent, handler: ValueChangeHandler) {
      self.eventEmitter.off(event, handler);
    },
    /** Notify listeners of a value change */
    notifyValueChange(path: string, value: any, context: ValueChangeContext & { initial: boolean }) {
      self.eventEmitter.emit(!context.initial ? 'value' : 'initial', path, value, context);
    }
  }));
